import { useParams, useNavigate } from 'react-router-dom';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { categories, hotComics, siteInfo } from '@/mocks/comics';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();

  const currentCategory = categories.find((c) => c.slug === slug);
  const displayName = currentCategory ? currentCategory.name : '全部';
  const displayIcon = currentCategory ? currentCategory.icon : 'ri-apps-line';

  const displayComics = slug === 'all' ? hotComics : hotComics.filter((c) => {
    const cat = categories.find((cat) => cat.name === c.category);
    return cat?.slug === slug;
  });

  const finalComics = displayComics.length > 0 ? displayComics : hotComics;

  const breadcrumbItems = [
    { name: '分类浏览', path: '/category/all' },
    { name: `${displayName}漫画` },
  ];

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: '首页',
        item: 'http://homekleans.com',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: '分类浏览',
        item: `${'http://homekleans.com'}/category/all`,
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: `${displayName}漫画`,
        item: 'http://homekleans.com/',
      },
    ],
  };

  const webpageSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${displayName}漫画 - ${siteInfo.name}`,
    description: `叮当漫画网${displayName}漫画分类，汇集海量${displayName}类型无删减漫画，下拉式免费阅读。`,
    url: 'http://homekleans.com/',
    publisher: {
      '@type': 'Organization',
      name: siteInfo.name,
    },
    mainEntity: {
      '@type': 'ItemList',
      itemListElement: finalComics.map((comic, idx) => ({
        '@type': 'ListItem',
        position: idx + 1,
        item: {
          '@type': 'CreativeWork',
          name: comic.title,
          url: `${'http://homekleans.com'}/comic/${comic.slug}`,
          author: { '@type': 'Person', name: comic.author },
          aggregateRating: {
            '@type': 'AggregateRating',
            ratingValue: comic.rating,
            bestRating: 10,
          },
        },
      })),
    },
  };

  return (
    <>
      <script type="application/ld+json">
        {JSON.stringify(breadcrumbSchema)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(webpageSchema)}
      </script>
      <Navbar />
      <main>
        <div className="max-w-7xl mx-auto px-4 md:px-6 pt-20 pb-16">
          <Breadcrumb items={breadcrumbItems} />

          <div className="flex items-center gap-3 mb-6 mt-4">
            <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-600">
              <i className={`${displayIcon} text-lg`}></i>
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-heading font-black text-foreground-950">
                {displayName}漫画
              </h1>
              <p className="text-sm text-foreground-500 mt-1">
                共 {finalComics.length} 部作品
              </p>
            </div>
          </div>

          {/* Category Tabs */}
          <div className="flex flex-wrap gap-2 mb-8 pb-4 border-b border-background-200/70">
            <button
              onClick={() => navigate('/category/all')}
              className={`px-4 py-2 text-sm font-medium rounded-full whitespace-nowrap transition-all duration-200 cursor-pointer ${
                slug === 'all'
                  ? 'bg-primary-500 text-background-50'
                  : 'bg-background-100 text-foreground-600 hover:bg-background-200'
              }`}
            >
              全部
            </button>
            {categories.map((cat) => (
              <button
                key={cat.slug}
                onClick={() => navigate(`/category/${cat.slug}`)}
                className={`px-4 py-2 text-sm font-medium rounded-full whitespace-nowrap transition-all duration-200 cursor-pointer ${
                  slug === cat.slug
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                {cat.name}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-5">
            {finalComics.map((comic) => (
              <Link
                key={comic.id}
                to={`/comic/${comic.slug}`}
                className="group"
              >
                <div className="relative overflow-hidden rounded-lg mb-3 aspect-[3/4] bg-background-200">
                  <img
                    src={comic.cover}
                    alt={comic.title}
                    title={`${comic.title} - 叮当漫画网`}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute top-2 left-2 flex flex-wrap gap-1">
                    {comic.tags.map((tag) => (
                      <span key={tag} className="px-2 py-0.5 bg-primary-500 text-background-50 text-[10px] font-medium rounded-full whitespace-nowrap">
                        {tag}
                      </span>
                    ))}
                  </div>
                  {comic.status === '连载中' && (
                    <span className="absolute top-2 right-2 px-2 py-0.5 bg-accent-500 text-background-50 text-[10px] font-medium rounded-full whitespace-nowrap">
                      连载
                    </span>
                  )}
                </div>
                <p className="text-xs text-foreground-500 mb-1">{comic.category} · {comic.author}</p>
                <h3 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors duration-200 whitespace-nowrap overflow-hidden text-ellipsis">
                  {comic.title}
                </h3>
                <p className="text-xs text-foreground-500 mt-0.5">更新至 {comic.chapters}话</p>
              </Link>
            ))}
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}